import React, { useCallback, useMemo, useState } from "react";
import { Accordion } from "react-bootstrap";
import { calculateTableWidth, CustomTableVirtuoso } from "../shared/table";
import { Complement } from "./domain/Complement";
import { HandleInputChangeRequest, UpdatedARIValue, ValueTypes } from "../shared/types";
import { ComplementData } from "../shared/domain/ComplementData";
import { AriType } from "../updateForm";
import { FormattedMessage, useIntl } from "react-intl";
import { messages } from "./messages";
import {
  TitleChevronContainer,
  Chevron,
  AccordionToggle,
  StyledFormControl,
  StyledInputGroupText,
  Container,
} from "./styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import InputGroup from "react-bootstrap/esm/InputGroup";

interface Props {
  items: ComplementData[];
  handleQuickUpdate: (ticker: string, valueType: ValueTypes, type: AriType) => void;
  enableEdition: boolean;
  handleInputChange: (props: HandleInputChangeRequest) => void;
  updatedARIValues: UpdatedARIValue;
  showAltStyles: boolean;
  numberOfDays: number;
}

export const Complements = ({
  items,
  handleQuickUpdate,
  enableEdition,
  handleInputChange,
  updatedARIValues,
  numberOfDays,
  showAltStyles,
}: Props): JSX.Element => {
  const intl = useIntl();
  const [toggleClosed, setToggleClosed] = React.useState(true);
  const [filterText, setFilterText] = useState("");

  const tableWidth = useMemo(() => calculateTableWidth(numberOfDays), [numberOfDays]);

  const complementsList = useMemo(() => {
    const allComplements = Object.values(items).map(it => Complement.of(it.ticker, it.dailySet));
    if (filterText.trim() === "") return allComplements;
    const lower = filterText.toLowerCase();
    return allComplements.filter(it => it.ticker.toLowerCase().includes(lower));
  }, [items, filterText]);

  const handleAccordionToggle = useCallback(() => {
    setToggleClosed(prev => !prev);
  }, []);

  return (
    <Accordion defaultActiveKey="0">
      <AccordionToggle
        as={Accordion.Toggle}
        variant="link"
        eventKey="0"
        width={tableWidth}
        onClick={handleAccordionToggle}>
        <TitleChevronContainer>
          <FormattedMessage {...messages.complements} />
          <Chevron toggleClosed={toggleClosed} />
        </TitleChevronContainer>
      </AccordionToggle>
      <Accordion.Collapse eventKey="0">
        <>
          <Container className="mt-2 mb-2">
            <InputGroup>
              <StyledInputGroupText id="search">
                <FontAwesomeIcon icon={faSearch} />
              </StyledInputGroupText>
              <StyledFormControl
                type="text"
                id="search"
                placeholder={intl.formatMessage(messages.filterPlaceholder)}
                onChange={e => setFilterText(e.target.value)}
                value={filterText}
              />
            </InputGroup>
          </Container>
          <CustomTableVirtuoso
            items={complementsList}
            handleQuickUpdate={handleQuickUpdate}
            enableEdition={enableEdition}
            handleInputChange={handleInputChange}
            updatedARIValues={updatedARIValues}
            isComplement
            showAltStyles={showAltStyles}
            numberOfDays={numberOfDays}
          />
        </>
      </Accordion.Collapse>
    </Accordion>
  );
};
